import React, { useMemo, useState } from 'react';
import { Box, Chip, Collapse, IconButton, Tooltip, Typography } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useFeatureTheme } from '../../../../theme/hooks/useFeatureTheme';
import { ANALYTICS_COPY } from '../../constants/analyticsCopy';
import {
  columnLabel,
  formatTableCell,
  hasBlankBreakdown,
  isTotalColumn,
  isValueColumn,
  reportTableModel,
  sortTableRows,
  sumColumn,
} from '../../utils/reportTableModel';
import { getAgentResponseBenchmark, parseDurationDisplay } from '../../utils/interveneFilters';
import AgentSessionsRowPanel from './AgentSessionsRowPanel';
import { analyticsService } from '../../services/analyticsService';

export default function ReportTableSection({
  result,
  compact = false,
  categoryLabel = '',
  measureLabel = '',
  columnLabels = {},
  valueFormat = 'number',
  querySpec = null,
  orgId = '',
}) {
  const { semantic, typography, radius } = useFeatureTheme();
  const model = useMemo(() => reportTableModel(result), [result]);
  const columns = model.columns;
  const [sort, setSort] = useState(null);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [expandedCustomers, setExpandedCustomers] = useState({});
  const [customerSessions, setCustomerSessions] = useState({});
  const [loadingCustomerSessions, setLoadingCustomerSessions] = useState({});
  const [sessionsByAgent, setSessionsByAgent] = useState({});
  const [sessionPages, setSessionPages] = useState({});
  const [loadingAgentSessions, setLoadingAgentSessions] = useState({});
  const [agentSessionErrors, setAgentSessionErrors] = useState({});

  const loadAgentSessions = async (rowKey, row, cursor = null) => {
    if (loadingAgentSessions[rowKey]) return;
    setLoadingAgentSessions((prev) => ({ ...prev, [rowKey]: true }));
    setAgentSessionErrors((prev) => ({ ...prev, [rowKey]: '' }));
    try {
      const username = row?.['Response Agent Username'] || row?.username || '';
      const agentUserId = querySpec?.filters?.find((item) => item.field === 'agent_user_id')?.value || '';
      const response = await analyticsService.getInterveneAgentSessions(
        querySpec,
        {
          responseAgentUsername: username,
          agentUserId,
          limit: tableMeta.session_page_size || 20,
          cursor,
          paginated: true,
        },
        orgId
      );
      if (!response?.ok) {
        throw new Error(response?.data?.message || response?.data || 'Could not load sessions.');
      }
      const page = response.data || {};
      setSessionsByAgent((prev) => ({
        ...prev,
        [rowKey]: cursor
          ? [...(prev[rowKey] || []), ...(page.sessions || [])]
          : (page.sessions || []),
      }));
      setSessionPages((prev) => ({
        ...prev,
        [rowKey]: { nextCursor: page.next_cursor || null, hasMore: Boolean(page.has_more) },
      }));
    } catch (error) {
      setAgentSessionErrors((prev) => ({
        ...prev,
        [rowKey]: error?.message || 'Could not load sessions.',
      }));
    } finally {
      setLoadingAgentSessions((prev) => ({ ...prev, [rowKey]: false }));
    }
  };

  const toggleRowExpand = (rowKey, row) => {
    const isCurrentlyExpanded = expandedRows.has(rowKey);
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(rowKey)) {
        next.delete(rowKey);
      } else {
        next.add(rowKey);
      }
      return next;
    });
    const hasInlineSessions = Array.isArray(row?.sessions);
    if (!isCurrentlyExpanded && isAgentBenchmark && !hasInlineSessions && !sessionsByAgent[rowKey]) {
      loadAgentSessions(rowKey, row);
    }
  };
  const rows = useMemo(() => {
    if (!sort?.column) return model.rows;
    return sortTableRows(model.rows, sort.column, sort.direction);
  }, [model.rows, sort]);
  const detailListing = columns.length > 0 && !columns.some((column) => isValueColumn(column));
  const showBlankNote = !detailListing && hasBlankBreakdown(columns, model.rows);
  const tableMeta = result?.meta || {};
  const isMasterDetail = tableMeta.detail_level === 'master';
  const blankField = categoryLabel || ANALYTICS_COPY.blankBreakdownField;
  const isDuration = valueFormat === 'duration';
  const isAgentBenchmark = useMemo(
    () => columns.some((c) => /response\s*agent\s*username/i.test(c))
      && columns.some((c) => /agent\s*response|first\s*reply/i.test(c)),
    [columns]
  );

  const agentReplyTotal = useMemo(() => {
    if (!isAgentBenchmark) return null;
    return rows.reduce((sum, row) => {
      const raw = row?.['Agent replies'] ?? row?.agent_replies ?? 0;
      const num = Number(raw);
      return sum + (Number.isFinite(num) ? num : 0);
    }, 0);
  }, [isAgentBenchmark, rows]);

  if (!rows.length) {
    return (
      <Typography
        sx={{
          fontSize: typography.fontSizeMd,
          color: semantic.text.secondary,
        }}
      >
        No rows to display.
      </Typography>
    );
  }

  if (isAgentBenchmark && agentReplyTotal === 0) {
    return (
      <Box
        sx={{
          p: 2.5,
          borderRadius: `${radius.md}px`,
          border: `1px dashed ${semantic.borders.border}`,
          bgcolor: semantic.surfaces.surfaceMuted,
        }}
      >
        <Typography sx={{ fontWeight: typography.fontWeightSemibold, mb: 0.75 }}>
          {ANALYTICS_COPY.interveneBenchmarkEmptyTitle}
        </Typography>
        <Typography sx={{ fontSize: typography.fontSizeMd, color: semantic.text.secondary }}>
          {ANALYTICS_COPY.interveneBenchmarkEmptySteps}
        </Typography>
      </Box>
    );
  }

  const toggleSort = (column) => {
    setSort((current) => {
      if (current?.column !== column) return { column, direction: 'asc' };
      if (current.direction === 'asc') return { column, direction: 'desc' };
      return null;
    });
  };

  const toggleCustomerExpand = async (rowKey, agentName, phoneNumber) => {
    const customerKey = `${rowKey}::${phoneNumber}`;
    setExpandedCustomers((prev) => ({
      ...prev,
      [customerKey]: !prev[customerKey],
    }));
    if (customerSessions[customerKey] || loadingCustomerSessions[customerKey]) {
      return;
    }
    setLoadingCustomerSessions((prev) => ({ ...prev, [customerKey]: true }));
    try {
      const response = await analyticsService.getInterveneAgentSessions(
        querySpec,
        {
          responseAgentUsername: agentName,
          phoneNumber,
          limit: tableMeta.lazy_session_cap || 20,
        },
        orgId
      );
      const sessions = response?.data?.sessions || [];
      setCustomerSessions((prev) => ({ ...prev, [customerKey]: sessions }));
    } finally {
      setLoadingCustomerSessions((prev) => ({ ...prev, [customerKey]: false }));
    }
  };

  return (
    <Box>
      <Box
        className="analytics-report-table"
        sx={{
          width: '100%',
          overflow: 'auto',
          maxHeight: compact ? 520 : 680,
          bgcolor: semantic.surfaces.surface,
          borderRadius: compact ? 0 : `${radius.md}px`,
          border: compact ? 'none' : `1px solid ${semantic.borders.border}`,
        }}
      >
        <Box
          component="table"
          sx={{
            display: 'table',
            width: 'max-content',
            minWidth: '100%',
            tableLayout: 'auto',
            borderCollapse: 'collapse',
            fontSize: typography.fontSizeMd,
            fontFamily: typography.fontFamilyBase,
            '& th, & td': {
              boxSizing: 'border-box',
              verticalAlign: 'middle',
            },
            '& > thead': {
              position: 'relative',
              zIndex: 11,
            },
            '& thead th': {
              position: 'sticky',
              top: 0,
              zIndex: 2,
              backgroundColor: `${semantic.interactive.primary} !important`,
              backgroundImage: 'none !important',
              color: `${semantic.interactive.primaryForeground} !important`,
              fontWeight: typography.fontWeightSemibold,
              fontSize: typography.fontSizeSm,
              py: 0,
              px: 0,
              border: 'none',
              whiteSpace: 'nowrap',
            },
            '& thead th.is-value, & tbody td.is-value, & tfoot td.is-value': {
              fontVariantNumeric: 'tabular-nums',
            },
            '& tbody td, & tfoot td': {
              py: 1,
              px: 1.25,
              borderBottom: `1px solid ${semantic.borders.border}`,
              color: semantic.text.primary,
              backgroundColor: semantic.surfaces.surface,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            },
            '& tbody td.is-category': {
              fontWeight: typography.fontWeightMedium,
            },
            '& tfoot td': {
              fontWeight: typography.fontWeightSemibold,
              backgroundColor: semantic.surfaces.surfaceMuted,
              borderBottom: 'none',
            },
            '& tbody tr:hover td': {
              backgroundColor: semantic.surfaces.surfaceMuted,
            },
          }}
        >
          <thead>
            <tr>
              {columns.map((col, colIndex) => {
                const active = sort?.column === col;
                const isValue = isValueColumn(col);
                const isFirstCol = colIndex === 0;
                return (
                  <Box
                    component="th"
                    key={col}
                    scope="col"
                    aria-sort={active ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
                    className={isValue ? 'is-value' : undefined}
                    sx={{
                      textAlign: isFirstCol ? 'left !important' : 'center !important',
                      minWidth: isFirstCol ? 180 : 90,
                    }}
                  >
                    <Box
                      component="button"
                      type="button"
                      title={
                        /^chats$/i.test(col)
                          ? 'Sessions where this agent has message activity'
                          : /sessions?\s*started/i.test(col)
                            ? 'Intervene sessions started by this agent'
                            : undefined
                      }
                      onClick={() => toggleSort(col)}
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: isFirstCol ? 'flex-start' : 'center',
                        width: '100%',
                        m: 0,
                        py: 1,
                        px: 1.25,
                        border: 'none',
                        background: 'transparent',
                        color: 'inherit',
                        font: 'inherit',
                        fontWeight: 'inherit',
                        textAlign: isFirstCol ? 'left' : 'center',
                        whiteSpace: 'nowrap',
                        cursor: 'pointer',
                        '&:focus-visible': {
                          outline: `2px solid ${semantic.interactive.primaryForeground}`,
                          outlineOffset: -2,
                        },
                      }}
                    >
                      {columnLabel(col, { categoryLabel, measureLabel, columnLabels })}
                      {active ? (sort.direction === 'asc' ? ' ↑' : ' ↓') : ''}
                    </Box>
                  </Box>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => {
              const agentKey = row?.['Response Agent Username'] || row?.username || ('row-' + idx);
              const isExpanded = expandedRows.has(agentKey);
              const inlineSessions = Array.isArray(row?.sessions) ? row.sessions : null;
              const sessions = inlineSessions || sessionsByAgent[agentKey] || [];
              const hasSessions = sessions.length > 0;
              const sessionCount = Number(
                row?.['Sessions started'] ?? row?.['Number of sessions'] ?? row?.['Chats'] ?? 0
              );
              const canExpandSessions = hasSessions || (isAgentBenchmark && sessionCount > 0);
              const hasCustomers = Array.isArray(row?.customers) && row.customers.length > 0;

              return (
                <React.Fragment key={agentKey}>
                  <tr
                    style={{
                      backgroundColor: isExpanded ? 'rgba(5, 150, 105, 0.04)' : undefined,
                      transition: 'background-color 0.2s',
                    }}
                  >
                    {columns.map((col, colIndex) => {
                      const isValue = isValueColumn(col);
                      const isFirstCol = colIndex === 0;
                      const raw = row && typeof row === 'object' ? row[col] : row;
                      const isSessionCol = col === 'Sessions started'
                        || col === 'Number of sessions'
                        || /^sessions?\s*started$/i.test(col);
                      const durationSeconds = isDuration ? parseDurationDisplay(raw) : null;
                      const benchmark = isDuration && durationSeconds
                        ? getAgentResponseBenchmark(durationSeconds)
                        : (isValue && isDuration && typeof raw === 'number'
                          ? getAgentResponseBenchmark(raw)
                          : null);

                      return (
                        <Box
                          component="td"
                          key={col}
                          className={isValue ? 'is-value' : 'is-category'}
                          sx={{
                            textAlign: isFirstCol ? 'left !important' : 'center !important',
                          }}
                        >
                          {isFirstCol ? (
                            formatTableCell(raw, isValue, valueFormat)
                          ) : isSessionCol && (canExpandSessions || hasCustomers) ? (
                            <Tooltip
                              title={isExpanded ? 'Hide details' : (hasCustomers ? 'View customers' : 'View sessions')}
                              arrow
                            >
                              <Box
                                component="button"
                                type="button"
                                  onClick={(e) => {
                                  e.stopPropagation();
                                    toggleRowExpand(agentKey, row);
                                }}
                                sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  gap: 0.75,
                                  px: 1.25,
                                  py: 0.35,
                                  border: '1px solid',
                                  borderColor: isExpanded ? '#059669' : 'rgba(5, 150, 105, 0.35)',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  bgcolor: isExpanded ? '#dcfce7' : 'rgba(5, 150, 105, 0.08)',
                                  color: isExpanded ? '#15803d' : '#059669',
                                  font: 'inherit',
                                  fontWeight: 600,
                                  transition: 'all 0.15s ease-in-out',
                                  '&:hover': {
                                    bgcolor: isExpanded ? '#bbf7d0' : 'rgba(5, 150, 105, 0.18)',
                                    borderColor: '#059669',
                                  },
                                }}
                              >
                                <Box
                                  component="span"
                                  sx={{
                                    bgcolor: isExpanded ? '#15803d' : '#059669',
                                    color: '#ffffff',
                                    px: 0.6,
                                    py: 0.1,
                                    borderRadius: '4px',
                                    fontSize: '0.75rem',
                                    fontWeight: 700,
                                    lineHeight: 1.2,
                                  }}
                                >
                                  {formatTableCell(raw, isValue, valueFormat)}
                                </Box>
                                <Typography
                                  component="span"
                                  sx={{
                                    fontSize: '0.6875rem',
                                    fontWeight: 600,
                                    color: isExpanded ? '#15803d' : '#047857',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {isExpanded ? 'Hide Details' : (hasCustomers ? 'View Customers' : 'View Sessions')}
                                </Typography>
                                {isExpanded ? (
                                  <KeyboardArrowUpIcon sx={{ fontSize: 16 }} />
                                ) : (
                                  <KeyboardArrowDownIcon sx={{ fontSize: 16 }} />
                                )}
                              </Box>
                            </Tooltip>
                          ) : detailListing && (raw == null || raw === '') ? (
                            '—'
                          ) : isDuration && benchmark && /response|reply/i.test(col) ? (
                            <Box
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 1,
                                width: '100%',
                              }}
                            >
                              <span>{formatTableCell(raw, isValue, valueFormat)}</span>
                              <Chip
                                size="small"
                                label={benchmark.label}
                                sx={{
                                  fontSize: '0.6875rem',
                                  fontWeight: 700,
                                  color: benchmark.color,
                                  bgcolor: benchmark.bg,
                                  height: 20,
                                  border: '1px solid ' + benchmark.color + '40',
                                }}
                              />
                            </Box>
                          ) : (
                            formatTableCell(raw, isValue, valueFormat)
                          )}
                        </Box>
                      );
                    })}
                  </tr>

                  {(canExpandSessions || isExpanded && isAgentBenchmark) && (
                    <tr key={agentKey + '-sessions-collapse'}>
                      <Box
                        component="td"
                        colSpan={columns.length}
                        sx={{
                          p: '0 !important',
                          border: 'none !important',
                          bgcolor: 'transparent',
                        }}
                      >
                        <Collapse in={isExpanded} timeout="auto">
                          <AgentSessionsRowPanel
                            agentName={row?.['Response Agent Username'] || row?.username || 'Agent'}
                            sessions={sessions}
                            row={row}
                            chats={row?.['Chats'] ?? row?.chats}
                            sessionsStarted={row?.['Sessions started'] ?? row?.['Number of sessions'] ?? row?.sessions_started}
                            isLoadingSessions={Boolean(loadingAgentSessions[agentKey])}
                            sessionsError={agentSessionErrors[agentKey] || ''}
                            hasMoreSessions={Boolean(sessionPages[agentKey]?.hasMore)}
                            onLoadMoreSessions={() => loadAgentSessions(
                              agentKey,
                              row,
                              sessionPages[agentKey]?.nextCursor
                            )}
                            querySpec={querySpec}
                            orgId={orgId}
                            replyPageSize={tableMeta.reply_page_size || 20}
                          />
                        </Collapse>
                      </Box>
                    </tr>
                  )}
                  {isMasterDetail && hasCustomers && (
                    <tr key={agentKey + '-customers-collapse'}>
                      <Box
                        component="td"
                        colSpan={columns.length}
                        sx={{
                          p: '0 !important',
                          border: 'none !important',
                          bgcolor: 'transparent',
                        }}
                      >
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <Box sx={{ p: 2, bgcolor: '#f8fafc', borderLeft: '4px solid #6366f1' }}>
                            <Typography sx={{ fontWeight: 700, mb: 1 }}>
                              Customer summary ({row.customers.length})
                            </Typography>
                            <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
                              <thead>
                                <tr>
                                  {['Customer', 'Replies', 'Sessions', 'Avg agent response', 'Avg customer response', 'Last activity', ''].map((head) => (
                                    <Box component="th" key={head} sx={{ textAlign: 'left', fontSize: '0.75rem', py: 0.75, px: 1 }}>
                                      {head}
                                    </Box>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {row.customers.map((customer, cIndex) => {
                                  const phone = customer?.phone_number || `customer-${cIndex}`;
                                  const customerKey = `${agentKey}::${phone}`;
                                  const customerOpen = !!expandedCustomers[customerKey];
                                  const sessions = customerSessions[customerKey] || [];
                                  const isLoading = !!loadingCustomerSessions[customerKey];
                                  return (
                                    <React.Fragment key={customerKey}>
                                      <tr>
                                        <Box component="td" sx={{ px: 1, py: 0.75 }}>{phone}</Box>
                                        <Box component="td" sx={{ px: 1, py: 0.75 }}>{customer.reply_count || 0}</Box>
                                        <Box component="td" sx={{ px: 1, py: 0.75 }}>{customer.session_count || 0}</Box>
                                        <Box component="td" sx={{ px: 1, py: 0.75 }}>{customer.avg_agent_response_display || '—'}</Box>
                                        <Box component="td" sx={{ px: 1, py: 0.75 }}>{customer.avg_customer_response_display || '—'}</Box>
                                        <Box component="td" sx={{ px: 1, py: 0.75 }}>{customer.last_activity_at || '—'}</Box>
                                        <Box component="td" sx={{ px: 1, py: 0.75 }}>
                                          <button
                                            type="button"
                                            onClick={() => toggleCustomerExpand(agentKey, row?.['Response Agent Username'] || row?.username || '', phone)}
                                            style={{ border: 'none', background: 'transparent', color: '#2563eb', cursor: 'pointer' }}
                                          >
                                            {customerOpen ? 'Hide sessions' : 'View sessions'}
                                          </button>
                                        </Box>
                                      </tr>
                                      <tr>
                                        <Box component="td" colSpan={7} sx={{ p: 0, border: 'none' }}>
                                          <Collapse in={customerOpen} timeout="auto" unmountOnExit>
                                            {isLoading ? (
                                              <Typography sx={{ p: 1, fontSize: '0.8rem' }}>Loading sessions...</Typography>
                                            ) : (
                                              <AgentSessionsRowPanel
                                                agentName={row?.['Response Agent Username'] || row?.username || 'Agent'}
                                                sessions={sessions}
                                                row={row}
                                                chats={sessions.length}
                                                sessionsStarted={customer.session_count || sessions.length}
                                              />
                                            )}
                                          </Collapse>
                                        </Box>
                                      </tr>
                                    </React.Fragment>
                                  );
                                })}
                              </tbody>
                            </Box>
                            {row.customers_truncated ? (
                              <Typography sx={{ mt: 1, fontSize: '0.75rem', color: '#64748b' }}>
                                Showing top {row.customers.length} customers due to cap.
                              </Typography>
                            ) : null}
                          </Box>
                        </Collapse>
                      </Box>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
          {detailListing ? null : (
            <tfoot>
              <tr>
                {columns.map((col, index) => {
                  const isValue = isValueColumn(col) || isTotalColumn(col, index, model.rows);
                  const showTotal = isTotalColumn(col, index, model.rows);
                  return (
                    <Box
                      component="td"
                      key={col}
                      className={isValue ? 'is-value' : undefined}
                      sx={{
                        textAlign: index === 0 ? 'left !important' : 'center !important',
                      }}
                    >
                      {index === 0 && !showTotal
                        ? ANALYTICS_COPY.tableTotal
                        : showTotal
                          ? formatTableCell(sumColumn(model.rows, col), true, valueFormat)
                          : ''}
                    </Box>
                  );
                })}
              </tr>
            </tfoot>
          )}
        </Box>
      </Box>
      {showBlankNote ? (
        <Typography
          sx={{
            mt: 1,
            px: compact ? 1.75 : 0,
            fontSize: typography.fontSizeSm,
            color: semantic.text.secondary,
          }}
        >
          {ANALYTICS_COPY.blankBreakdownNote.replace('{field}', blankField)}
        </Typography>
      ) : null}
    </Box>
  );
}
